import { formatAsn } from '../../../formatter';
import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import { createAsnFilter } from '../../filters/types/asnFilter';
import type {
  TableColumnFormatLink,
  TableLinkColumnConfig,
} from '../../links/index';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';
import { mergeFieldDependencies } from './helpers/mergeFieldDependencies';

export type TableColumnFormatAsn<TRow> = {
  type: 'asn';
  field: KeyOfType<TRow, number | number[] | null>;
} & TableColumnFormatLink<TRow>;

type AsnColumnConfig<TRow, TDataType> = TableDataColumnConfig<TRow, TDataType> &
  TableLinkColumnConfig<TRow>;
export const createAsnColumn: {
  <TRow>(
    config: AsnColumnConfig<TRow, number | null> & { array?: false },
  ): TableColumnBase<TRow, TableColumnFormatAsn<TRow>>;
  <TRow>(
    config: AsnColumnConfig<TRow, number[] | null> & { array: true },
  ): TableColumnBase<TRow, TableColumnFormatAsn<TRow>>;
} = <TRow>(
  config: AsnColumnConfig<TRow, number | number[] | null>,
): TableColumnBase<TRow, TableColumnFormatAsn<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'asn',
    field: config.key,
    createLinkProps: config.link?.createLinkProps,
  },
  csvStringDependencies: [config.key],
  webDependencies: mergeFieldDependencies(
    config.key,
    config.link?.dependencies,
  ),
  toCsvString: (row, context) =>
    formatValueToCsvString(
      getValueFromKeyOfType<TRow, number | number[] | null>(row, config.key),
      (v: number | null): string => formatAsn(v, context.asnFormat) ?? '',
    ),
  filter: createDefaultFilter(config.filter, createAsnFilter()),
});
